import { useEffect, useRef, useState } from 'react';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Save, 
  Upload, 
  Image, 
  Video, 
  X, 
  Plus,
  Tag,
  Wifi,
  Car,
  Wind,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Search
} from 'lucide-react';
import type { Restaurant } from '../../App';
import { api } from '../../lib/api';
import { resolveAssetUrl } from '../../lib/assets';
import { MapCoordinatePicker } from '../common/MapCoordinatePicker';

type RestaurantSettingsModel = Restaurant & {
  website?: string;
  state?: string;
  country?: string;
  cityId?: string;
  stateId?: string;
  countryId?: string;
  latitude?: number | null;
  longitude?: number | null;
  locationMaster?: {
    cityId?: string;
    stateId?: string;
    countryId?: string;
  };
  zipCode?: string;
  priceRange?: string;
  priceRangeMaster?: { name?: string; symbol?: string };
  foodTypes?: Array<string | { id?: string; code?: string; name?: string }>;
  coverImage?: string;
};

type MasterOption = {
  id: string;
  name: string;
  code?: string;
};

type LocationOption = {
  id: string;
  name: string;
  countryId?: string | null;
  stateId?: string | null;
};

type FoodTypeOption = {
  id: string;
  name: string;
  code?: string;
};

type PriceRangeOption = {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
};

type CurrencyOption = {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
  decimalPlaces?: number | null;
};

type GalleryMediaItem = {
  id?: string;
  mediaUrl: string;
  mediaType?: 'PHOTO' | 'VIDEO';
  fileName?: string | null;
};

const normalizeApiFileUrl = (value: string): string => {
  return resolveAssetUrl(value);
};

const toGalleryMediaItems = (list: any, fallbackType: 'PHOTO' | 'VIDEO'): GalleryMediaItem[] => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item: any) => {
      const rawUrl =
        typeof item === 'string'
          ? item
          : String(item?.mediaUrl || item?.url || '');
      const mediaUrl = normalizeApiFileUrl(rawUrl);
      if (!mediaUrl) return null;
      return {
        id: item?.id ? String(item.id) : undefined,
        mediaUrl,
        mediaType: (item?.mediaType as 'PHOTO' | 'VIDEO') || fallbackType,
        fileName: item?.fileName ? String(item.fileName) : null,
      } as GalleryMediaItem;
    })
    .filter(Boolean) as GalleryMediaItem[];
};

interface RestaurantSettingsProps {
  restaurant: RestaurantSettingsModel;
  onRestaurantUpdated?: (restaurant: any) => void;
}

export function RestaurantSettings({ restaurant, onRestaurantUpdated }: RestaurantSettingsProps) {
  const parseList = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'string' ? item : item?.id))
        .map((item) => String(item ?? '').trim())
        .filter(Boolean);
    }
    if (value === undefined || value === null) return [];
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const initialCuisineValues = () =>
    parseList((restaurant as any)?.cuisine).length
      ? parseList((restaurant as any)?.cuisine)
      : parseList((restaurant as any)?.cuisineType);

  const initialAmenityValues = () => parseList((restaurant as any)?.amenities);

  const initialFoodTypeValues = () =>
    parseList((restaurant as any)?.foodTypeIds).length
      ? parseList((restaurant as any)?.foodTypeIds)
      : parseList((restaurant as any)?.foodTypes);

  const [restaurantName, setRestaurantName] = useState(restaurant.name || '');
  const [description, setDescription] = useState(restaurant.description || '');
  const [address, setAddress] = useState(restaurant.address || '');
  const [city, setCity] = useState(restaurant.city || '');
  const [state, setState] = useState(restaurant.state || '');
  const [country, setCountry] = useState(restaurant.country || '');
  const [selectedCountryId, setSelectedCountryId] = useState(
    restaurant.locationMaster?.countryId || restaurant.countryId || ''
  );
  const [selectedStateId, setSelectedStateId] = useState(
    restaurant.locationMaster?.stateId || restaurant.stateId || ''
  );
  const [selectedCityId, setSelectedCityId] = useState(
    restaurant.locationMaster?.cityId || restaurant.cityId || ''
  );
  const [countryOptions, setCountryOptions] = useState<LocationOption[]>([]);
  const [stateOptions, setStateOptions] = useState<LocationOption[]>([]);
  const [cityOptions, setCityOptions] = useState<LocationOption[]>([]);
  const [zipCode, setZipCode] = useState(restaurant.zipCode || '');
  const [latitude, setLatitude] = useState(
    restaurant.latitude !== undefined && restaurant.latitude !== null ? String(restaurant.latitude) : ''
  );
  const [longitude, setLongitude] = useState(
    restaurant.longitude !== undefined && restaurant.longitude !== null ? String(restaurant.longitude) : ''
  );
  const [phone, setPhone] = useState(restaurant.phone || '');
  const [email, setEmail] = useState(restaurant.email || '');
  const [website, setWebsite] = useState(restaurant.website || '');
  const [openTime, setOpenTime] = useState(restaurant.openTime || '');
  const [closeTime, setCloseTime] = useState(restaurant.closeTime || '');
  const [deliveryTime, setDeliveryTime] = useState(restaurant.deliveryTime || '');
  const [minimumOrder, setMinimumOrder] = useState(String(restaurant.minimumOrder ?? ''));
  const [deliveryFee, setDeliveryFee] = useState(String(restaurant.deliveryFee ?? ''));
  const [packagingCharges, setPackagingCharges] = useState(String(restaurant.packagingCharges ?? ''));
  const [selectedPriceRangeId, setSelectedPriceRangeId] = useState(
    (restaurant as any)?.priceRangeId || (restaurant as any)?.priceRangeMaster?.id || ''
  );
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(
    (restaurant as any)?.currencyId || (restaurant as any)?.currency?.id || (restaurant as any)?.currencyMaster?.id || ''
  );
  
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>(initialCuisineValues);
  const [selectedFoodTypes, setSelectedFoodTypes] = useState<string[]>(initialFoodTypeValues);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>(initialAmenityValues);
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mediaLogo, setMediaLogo] = useState(restaurant.logo || '');
  const [mediaBanner, setMediaBanner] = useState(restaurant.banner || restaurant.coverImage || '');
  const [mediaUploading, setMediaUploading] = useState<'logo' | 'banner' | null>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<GalleryMediaItem[]>(
    toGalleryMediaItems((restaurant as any)?.photos, 'PHOTO')
  );
  const [galleryVideos, setGalleryVideos] = useState<GalleryMediaItem[]>(
    toGalleryMediaItems((restaurant as any)?.videos, 'VIDEO')
  );
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryVideoUploading, setGalleryVideoUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const photosInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const foodTypeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [masterCuisines, setMasterCuisines] = useState<MasterOption[]>([]);
  const [masterAmenities, setMasterAmenities] = useState<MasterOption[]>([]);
  const [foodTypeOptions, setFoodTypeOptions] = useState<FoodTypeOption[]>([]);
  const [priceRangeOptions, setPriceRangeOptions] = useState<PriceRangeOption[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([]);
  const [foodTypeDropdownOpen, setFoodTypeDropdownOpen] = useState(false);
  const [foodTypeSearch, setFoodTypeSearch] = useState('');
  const [showCoordinatePicker, setShowCoordinatePicker] = useState(false);
  const [resolvingMapLocation, setResolvingMapLocation] = useState(false);

  const resolveAddressToCoords = async () => {
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { latitude: lat, longitude: lng };
    }

    const addressQuery = [address, city, state, country, zipCode]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');
    if (!addressQuery) return null;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          addressQuery
        )}&limit=1&countrycodes=in`
      );
      if (!response.ok) return null;
      const rows = await response.json();
      const first = Array.isArray(rows) ? rows[0] : null;
      const resolvedLat = Number(first?.lat);
      const resolvedLng = Number(first?.lon);
      if (!Number.isFinite(resolvedLat) || !Number.isFinite(resolvedLng)) return null;
      return { latitude: resolvedLat, longitude: resolvedLng };
    } catch {
      return null;
    }
  };

  const openMapPicker = async () => {
    setResolvingMapLocation(true);
    const coords = await resolveAddressToCoords();
    if (coords) {
      setLatitude(String(coords.latitude));
      setLongitude(String(coords.longitude));
    }
    setResolvingMapLocation(false);
    setShowCoordinatePicker(true);
  };

  useEffect(() => {
    setRestaurantName(restaurant.name || '');
    setDescription(restaurant.description || '');
    setAddress(restaurant.address || '');
    setCity(restaurant.city || '');
    setState(restaurant.state || '');
    setCountry(restaurant.country || '');
    setSelectedCountryId(restaurant.locationMaster?.countryId || restaurant.countryId || '');
    setSelectedStateId(restaurant.locationMaster?.stateId || restaurant.stateId || '');
    setSelectedCityId(restaurant.locationMaster?.cityId || restaurant.cityId || '');
    setZipCode(restaurant.zipCode || '');
    setLatitude(
      restaurant.latitude !== undefined && restaurant.latitude !== null ? String(restaurant.latitude) : ''
    );
    setLongitude(
      restaurant.longitude !== undefined && restaurant.longitude !== null ? String(restaurant.longitude) : ''
    );
    setPhone(restaurant.phone || '');
    setEmail(restaurant.email || '');
    setWebsite(restaurant.website || '');
    setOpenTime(restaurant.openTime || '');
    setCloseTime(restaurant.closeTime || '');
    setDeliveryTime(restaurant.deliveryTime || '');
    setMinimumOrder(String(restaurant.minimumOrder ?? ''));
    setDeliveryFee(String(restaurant.deliveryFee ?? ''));
    setPackagingCharges(String(restaurant.packagingCharges ?? ''));
    setSelectedPriceRangeId((restaurant as any)?.priceRangeId || (restaurant as any)?.priceRangeMaster?.id || '');
    setSelectedCurrencyId(
      (restaurant as any)?.currencyId ||
        (restaurant as any)?.currency?.id ||
        (restaurant as any)?.currencyMaster?.id ||
        ''
    );
    setSelectedCuisines(
      parseList((restaurant as any)?.cuisine).length
        ? parseList((restaurant as any)?.cuisine)
        : parseList((restaurant as any)?.cuisineType)
    );
    setSelectedFoodTypes(
      parseList((restaurant as any)?.foodTypeIds).length
        ? parseList((restaurant as any)?.foodTypeIds)
        : parseList((restaurant as any)?.foodTypes)
    );
    setSelectedAmenities(parseList((restaurant as any)?.amenities));
    setMediaLogo(restaurant.logo || '');
    setMediaBanner(restaurant.banner || restaurant.coverImage || '');
    setGalleryPhotos(toGalleryMediaItems((restaurant as any)?.photos, 'PHOTO'));
    setGalleryVideos(toGalleryMediaItems((restaurant as any)?.videos, 'VIDEO'));
  }, [
    restaurant.id,
    restaurant.name,
    restaurant.address,
    restaurant.city,
    restaurant.state,
    restaurant.country,
    restaurant.zipCode,
    restaurant.latitude,
    restaurant.longitude,
    restaurant.phone,
    restaurant.email,
    restaurant.website,
    restaurant.openTime,
    restaurant.closeTime,
    restaurant.deliveryTime,
    restaurant.minimumOrder,
    restaurant.deliveryFee,
    restaurant.packagingCharges,
    restaurant.logo,
    restaurant.banner,
    restaurant.coverImage,
    (restaurant as any).updatedAt,
  ]);

  const loadLocationOptions = async (params?: { countryId?: string; stateId?: string }) => {
    try {
      const response = await api.restaurants.getLocationOptions(params);
      const location = response?.data || { countries: [], states: [], cities: [], foodTypes: [], priceRanges: [] };

      const mappedCountries = (location.countries || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
      }));
      const mappedStates = (location.states || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        countryId: item.countryId ? String(item.countryId) : null,
      }));
      const mappedCities = (location.cities || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        stateId: item.stateId ? String(item.stateId) : null,
        countryId: item.countryId ? String(item.countryId) : null,
      }));
      const mappedFoodTypes = (location.foodTypes || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        code: item.code ? String(item.code) : undefined,
      }));
      const mappedPriceRanges = (location.priceRanges || []).map((item: any) => ({
        id: String(item.id),
        code: String(item.code || ''),
        name: String(item.name || item.code || item.id),
        symbol: item.symbol ? String(item.symbol) : null,
      }));

      setCountryOptions(mappedCountries);
      setStateOptions(mappedStates);
      setCityOptions(mappedCities);
      setFoodTypeOptions(mappedFoodTypes);
      setPriceRangeOptions(mappedPriceRanges);

      return { mappedCountries, mappedStates, mappedCities, mappedFoodTypes, mappedPriceRanges };
    } catch {
      return { mappedCountries: [], mappedStates: [], mappedCities: [], mappedFoodTypes: [], mappedPriceRanges: [] };
    }
  };
  
  useEffect(() => {
    let mounted = true;
    const loadMasterData = async () => {
      try {
        const [cuisineRes, amenityRes, currencyRes] = await Promise.all([
          api.masterData.cuisineTypes.getAll(),
          api.masterData.amenities.getAll(),
          api.masterData.currencies.getAll(),
        ]);
        if (!mounted) return;
        setMasterCuisines(Array.isArray(cuisineRes?.data) ? cuisineRes.data : []);
        setMasterAmenities(Array.isArray(amenityRes?.data) ? amenityRes.data : []);
        const currencyRows = Array.isArray(currencyRes?.data) ? currencyRes.data : [];
        const mappedCurrencies = currencyRows.map((item: any) => ({
          id: String(item.id),
          code: String(item.code || item.currency_code || ''),
          name: String(item.name || item.currency_name || item.code || item.id),
          symbol: item.symbol ? String(item.symbol) : null,
          decimalPlaces:
            item.decimalPlaces !== undefined && item.decimalPlaces !== null
              ? Number(item.decimalPlaces)
              : item.decimal_places !== undefined && item.decimal_places !== null
                ? Number(item.decimal_places)
                : null,
        }));
        setCurrencyOptions(mappedCurrencies);
        if (!selectedCurrencyId) {
          const preferred =
            mappedCurrencies.find((currency) => String(currency.code || '').toUpperCase() === 'INR') ||
            mappedCurrencies[0];
          if (preferred) setSelectedCurrencyId(preferred.id);
        }
        const locationData = await loadLocationOptions();
        if (!mounted) return;

        const resolvedCountryId =
          selectedCountryId ||
          locationData.mappedCountries.find((item) => item.name === country)?.id ||
          '';

        const resolvedStateId =
          selectedStateId ||
          locationData.mappedStates.find((item) => item.name === state)?.id ||
          '';

        const resolvedCityId =
          selectedCityId ||
          locationData.mappedCities.find((item) => item.name === city)?.id ||
          '';

        if (resolvedCountryId && resolvedCountryId !== selectedCountryId) {
          setSelectedCountryId(resolvedCountryId);
        }
        if (resolvedStateId && resolvedStateId !== selectedStateId) {
          setSelectedStateId(resolvedStateId);
        }
        if (resolvedCityId && resolvedCityId !== selectedCityId) {
          setSelectedCityId(resolvedCityId);
        }

        if (resolvedCountryId || resolvedStateId) {
          await loadLocationOptions({
            countryId: resolvedCountryId || undefined,
            stateId: resolvedStateId || undefined,
          });
        }
      } catch {
        // Keep fallback lists.
      }
    };
    void loadMasterData();
    return () => {
      mounted = false;
    };
  }, []);

  const cuisineOptions =
    masterCuisines.length > 0
      ? masterCuisines.map((item) => ({
          id: String(item.id),
          label: String(item.name || item.code || item.id),
          code: item.code ? String(item.code) : undefined,
        }))
      : [];

  const getAmenityIcon = (option: { label: string; code?: string }) => {
    const key = `${option.label} ${option.code || ''}`.toLowerCase();
    if (key.includes('wifi') || key.includes('wi-fi')) return Wifi;
    if (key.includes('parking')) return Car;
    if (key.includes('ac') || key.includes('air')) return Wind;
    if (key.includes('roof')) return Building2;
    return Building2;
  };

  const amenityOptions =
    masterAmenities.length > 0
      ? masterAmenities.map((item) => ({
          id: String(item.id),
          label: String(item.name || item.code || item.id),
          code: item.code ? String(item.code) : undefined,
          icon: getAmenityIcon({
            label: String(item.name || item.id),
            code: item.code ? String(item.code) : undefined,
          }),
        }))
      : [];

  const isCuisineSelected = (option: { id: string; label: string; code?: string }) =>
    selectedCuisines.includes(option.id) ||
    selectedCuisines.includes(option.label) ||
    (!!option.code && selectedCuisines.includes(option.code));

  const isAmenitySelected = (option: { id: string; label: string; code?: string }) =>
    selectedAmenities.includes(option.id) ||
    selectedAmenities.includes(option.label) ||
    (!!option.code && selectedAmenities.includes(option.code));

  const toggleCuisine = (option: { id: string; label: string; code?: string }) => {
    setSelectedCuisines(prev =>
      isCuisineSelected(option)
        ? prev.filter(
            (c) => c !== option.id && c !== option.label && (!option.code || c !== option.code)
          )
        : [...prev, option.id]
    );
  };

  const toggleAmenity = (option: { id: string; label: string; code?: string }) => {
    setSelectedAmenities(prev =>
      isAmenitySelected(option)
        ? prev.filter(
            (a) => a !== option.id && a !== option.label && (!option.code || a !== option.code)
          )
        : [...prev, option.id]
    );
  };

  const toggleFoodType = (foodTypeId: string) => {
    if (!foodTypeId) return;
    setSelectedFoodTypes((prev) => {
      const exists = prev.includes(foodTypeId);
      return exists ? prev.filter((id) => id !== foodTypeId) : [...prev, foodTypeId];
    });
  };

  const filteredFoodTypeOptions = foodTypeOptions.filter((item) =>
    item.name.toLowerCase().includes(foodTypeSearch.trim().toLowerCase())
  );
  const allVisibleFoodTypesSelected =
    filteredFoodTypeOptions.length > 0 &&
    filteredFoodTypeOptions.every((item) => selectedFoodTypes.includes(item.id));

  const toggleAllVisibleFoodTypes = () => {
    const visibleIds = filteredFoodTypeOptions.map((item) => item.id).filter(Boolean);
    if (visibleIds.length === 0) return;
    setSelectedFoodTypes((prev) => {
      const selectedSet = new Set(prev);
      const allSelected = visibleIds.every((id) => selectedSet.has(id));
      if (allSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      visibleIds.forEach((id) => selectedSet.add(id));
      return Array.from(selectedSet);
    });
  };

  const selectedFoodTypeNames = selectedFoodTypes
    .map((id) => foodTypeOptions.find((item) => item.id === id)?.name || id)
    .filter(Boolean);
  const foodTypeDropdownLabel =
    selectedFoodTypeNames.length === 0
      ? 'Select food types'
      : selectedFoodTypeNames.length <= 3
        ? selectedFoodTypeNames.join(', ')
        : `${selectedFoodTypeNames.length} items selected`;

  const selectedCurrency =
    currencyOptions.find((currency) => currency.id === selectedCurrencyId) ||
    currencyOptions.find((currency) => String(currency.code || '').toUpperCase() === 'INR') ||
    null;
  const currencySymbol = selectedCurrency?.symbol || selectedCurrency?.code || '₹';

  const unresolvedCuisineValues = selectedCuisines.filter(
    (selected) =>
      !cuisineOptions.some(
        (option) =>
          option.id === selected ||
          option.label === selected ||
          (!!option.code && option.code === selected)
      )
  );

  useEffect(() => {
    if (!foodTypeDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!foodTypeDropdownRef.current) return;
      const target = event.target as Node;
      if (!foodTypeDropdownRef.current.contains(target)) {
        setFoodTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [foodTypeDropdownOpen]);

  const handleCountryChange = async (countryId: string) => {
    setSelectedCountryId(countryId);
    const selectedCountry = countryOptions.find((item) => item.id === countryId);
    setCountry(selectedCountry?.name || '');
    setSelectedStateId('');
    setState('');
    setSelectedCityId('');
    setCity('');
    await loadLocationOptions({ countryId: countryId || undefined });
  };

  const handleStateChange = async (stateId: string) => {
    setSelectedStateId(stateId);
    const selectedState = stateOptions.find((item) => item.id === stateId);
    setState(selectedState?.name || '');
    setSelectedCityId('');
    setCity('');
    await loadLocationOptions({
      countryId: selectedCountryId || undefined,
      stateId: stateId || undefined,
    });
  };

  const handleCityChange = (cityId: string) => {
    setSelectedCityId(cityId);
    const selectedCity = cityOptions.find((item) => item.id === cityId);
    setCity(selectedCity?.name || '');
  };

  const handleSave = async () => {
    if (!restaurant.id) return;
    setIsSaving(true);
    setMediaError('');
    try {
      const response = await api.restaurants.update(String(restaurant.id), {
        name: restaurantName,
        description,
        phone,
        email,
        website,
        address,
        city,
        state,
        country,
        cityId: selectedCityId || undefined,
        stateId: selectedStateId || undefined,
        countryId: selectedCountryId || undefined,
        latitude:
          latitude.trim() === '' || Number.isNaN(Number(latitude))
            ? undefined
            : Number(latitude),
        longitude:
          longitude.trim() === '' || Number.isNaN(Number(longitude))
            ? undefined
            : Number(longitude),
        pincode: zipCode,
        openTime,
        closeTime,
        deliveryTime,
        priceRangeId: selectedPriceRangeId || undefined,
        currencyId: selectedCurrencyId || undefined,
        minimumOrder: Number(minimumOrder) || 0,
        deliveryFee: Number(deliveryFee) || 0,
        packagingCharges: Number(packagingCharges) || 0,
        logo: mediaLogo || undefined,
        banner: mediaBanner || undefined,
        coverImage: mediaBanner || undefined,
        cuisineType: selectedCuisines.join(','),
        foodTypeIds: selectedFoodTypes,
        amenities: selectedAmenities,
      });

      const saved = response?.data || {};
      if (saved && typeof saved === 'object') {
        const savedCuisine = parseList(saved.cuisine).length
          ? parseList(saved.cuisine)
          : parseList(saved.cuisineType);
        const savedAmenities = parseList(saved.amenities);
        const savedFoodTypes = parseList(saved.foodTypeIds).length
          ? parseList(saved.foodTypeIds)
          : parseList(saved.foodTypeId);
        setSelectedPriceRangeId(saved.priceRangeId || selectedPriceRangeId);
        setSelectedCurrencyId(saved.currencyId || saved.currency?.id || selectedCurrencyId);
        setSelectedCuisines(savedCuisine.length ? savedCuisine : selectedCuisines);
        setSelectedAmenities(savedAmenities.length ? savedAmenities : selectedAmenities);
        setSelectedFoodTypes(savedFoodTypes.length ? savedFoodTypes : selectedFoodTypes);
        setRestaurantName(saved.name || restaurantName);
        setDescription(saved.description || '');
        setAddress(saved.address || '');
        setCity(saved.city || '');
        setState(saved.state || '');
        setCountry(saved.country || '');
        setZipCode(saved.zipCode || saved.pincode || '');
        setLatitude(
          saved.latitude !== undefined && saved.latitude !== null ? String(saved.latitude) : ''
        );
        setLongitude(
          saved.longitude !== undefined && saved.longitude !== null ? String(saved.longitude) : ''
        );
        setPhone(saved.phone || '');
        setEmail(saved.email || '');
        setWebsite(saved.website || '');
        setOpenTime(saved.openTime || '');
        setCloseTime(saved.closeTime || '');
        setDeliveryTime(saved.deliveryTime || '');
        setMediaLogo(saved.logo || mediaLogo);
        setMediaBanner(saved.banner || saved.coverImage || mediaBanner);
        if (onRestaurantUpdated) {
          onRestaurantUpdated(saved);
        }
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } catch {
      setMediaError('Unable to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const uploadImageFile = async (file: File): Promise<string> => {
    const toDataUrl = (targetFile: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(targetFile);
      });

    const imageData = await toDataUrl(file);
    const response = await api.restaurants.uploadImage({
      imageData,
      fileName: file.name,
    });

    const uploadedUrl = String(response?.data?.url || '');
    return normalizeApiFileUrl(uploadedUrl);
  };

  const fileToDataUrl = (targetFile: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(targetFile);
    });

  const handleMediaUpload = async (file: File, field: 'logo' | 'banner') => {
    if (!file.type.startsWith('image/')) {
      setMediaError('Please upload a valid image file.');
      return;
    }

    setMediaError('');
    setMediaUploading(field);

    try {
      const normalizedUrl = await uploadImageFile(file);

      if (field === 'logo') {
        setMediaLogo(normalizedUrl);
      } else {
        setMediaBanner(normalizedUrl);
      }
    } catch {
      setMediaError('Unable to upload image. Please try again.');
    } finally {
      setMediaUploading(null);
    }
  };

  const handleGalleryPhotosUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!restaurant.id) return;
    setMediaError('');
    setGalleryUploading(true);
    try {
      const selectedFiles = Array.from(files).slice(0, 10);
      const uploadedItems: GalleryMediaItem[] = [];
      for (const file of selectedFiles) {
        if (!file.type.startsWith('image/')) continue;
        const fileData = await fileToDataUrl(file);
        const uploadResponse = await api.restaurants.uploadMediaFile({
          fileData,
          fileName: file.name,
        });
        const uploadedUrl = String(uploadResponse?.data?.url || '');
        const normalizedUrl = normalizeApiFileUrl(uploadedUrl);
        const created = await api.restaurants.addMedia(String(restaurant.id), {
          mediaType: 'PHOTO',
          mediaUrl: normalizedUrl,
          fileName: uploadResponse?.data?.fileName || file.name,
          mimeType: uploadResponse?.data?.mimeType || file.type,
          fileSize: uploadResponse?.data?.fileSize || file.size,
        });
        const rows = Array.isArray(created?.data) ? created.data : [];
        if (rows.length) {
          uploadedItems.push({
            id: rows[0]?.id,
            mediaUrl: String(rows[0]?.mediaUrl || normalizedUrl),
            mediaType: 'PHOTO',
            fileName: rows[0]?.fileName || file.name,
          });
        } else {
          uploadedItems.push({
            mediaUrl: normalizedUrl,
            mediaType: 'PHOTO',
            fileName: file.name,
          });
        }
      }
      if (uploadedItems.length === 0) {
        setMediaError('Please select valid image files for photos.');
      } else {
        const mediaResponse = await api.restaurants.getMedia(String(restaurant.id));
        const photoItems = toGalleryMediaItems(mediaResponse?.data?.photos, 'PHOTO');
        setGalleryPhotos((prev) =>
          photoItems.length ? photoItems.slice(0, 10) : [...uploadedItems, ...prev].slice(0, 10)
        );
      }
    } catch {
      setMediaError('Unable to upload gallery photos. Please try again.');
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryVideoSelect = (file?: File) => {
    void (async () => {
      if (!file || !restaurant.id) return;
      if (!file.type.startsWith('video/')) {
        setMediaError('Please select a valid video file.');
        return;
      }
      setMediaError('');
      setGalleryVideoUploading(true);
      try {
        const fileData = await fileToDataUrl(file);
        const uploadResponse = await api.restaurants.uploadMediaFile({
          fileData,
          fileName: file.name,
        });
        const uploadedUrl = String(uploadResponse?.data?.url || '');
        const normalizedUrl = normalizeApiFileUrl(uploadedUrl);
        const created = await api.restaurants.addMedia(String(restaurant.id), {
          mediaType: 'VIDEO',
          mediaUrl: normalizedUrl,
          fileName: uploadResponse?.data?.fileName || file.name,
          mimeType: uploadResponse?.data?.mimeType || file.type,
          fileSize: uploadResponse?.data?.fileSize || file.size,
        });
        const rows = Array.isArray(created?.data) ? created.data : [];
        const newItem: GalleryMediaItem = rows.length
          ? {
              id: rows[0]?.id,
              mediaUrl: normalizeApiFileUrl(String(rows[0]?.mediaUrl || normalizedUrl)),
              mediaType: 'VIDEO',
              fileName: rows[0]?.fileName || file.name,
            }
          : {
              mediaUrl: normalizedUrl,
              mediaType: 'VIDEO',
              fileName: file.name,
            };
        const mediaResponse = await api.restaurants.getMedia(String(restaurant.id));
        const videoItems = toGalleryMediaItems(mediaResponse?.data?.videos, 'VIDEO');
        setGalleryVideos((prev) => (videoItems.length ? videoItems : [newItem, ...prev]));
      } catch {
      setMediaError('Unable to upload video. Please try again.');
      } finally {
        setGalleryVideoUploading(false);
      }
    })();
  };

  const handleRemoveGalleryItem = async (item: GalleryMediaItem, mediaType: 'PHOTO' | 'VIDEO') => {
    if (!restaurant.id) return;
    try {
      if (item.id) {
        await api.restaurants.deleteMedia(String(restaurant.id), String(item.id));
      }
      if (mediaType === 'PHOTO') {
        setGalleryPhotos((prev) =>
          prev.filter((entry) => (item.id ? entry.id !== item.id : entry.mediaUrl !== item.mediaUrl))
        );
      } else {
        setGalleryVideos((prev) =>
          prev.filter((entry) => (item.id ? entry.id !== item.id : entry.mediaUrl !== item.mediaUrl))
        );
      }
    } catch {
      setMediaError('Unable to remove media item. Please try again.');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-gray-900 mb-1">Restaurant Settings</h1>
          <p className="text-gray-600">Manage your restaurant profile and settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-md"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 animate-pulse">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-green-900 font-medium">Settings saved successfully!</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Basic Information</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restaurant Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter restaurant name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="Describe your restaurant"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="contact@restaurant.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Location</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter full address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedCountryId}
                    onChange={(e) => {
                      void handleCountryChange(e.target.value);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select Country</option>
                    {countryOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedStateId}
                    disabled={!selectedCountryId}
                    onChange={(e) => {
                      void handleStateChange(e.target.value);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                  >
                    <option value="">Select State</option>
                    {stateOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedCityId}
                    disabled={!selectedStateId}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                  >
                    <option value="">Select City</option>
                    {cityOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zip Code
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter zip code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="text"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g. 28.6139000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="text"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g. 77.2090000"
                />
              </div>

              <div className="md:col-span-3">
                <button
                  type="button"
                  onClick={() => void openMapPicker()}
                  disabled={resolvingMapLocation}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-60"
                >
                  <MapPin className="w-4 h-4" />
                  {resolvingMapLocation ? 'Loading Map...' : 'View / Pick Location on Map'}
                </button>
              </div>

              {countryOptions.length === 0 && (
                <p className="text-sm text-amber-700">
                  Country/State/City master data not found.
                </p>
              )}
            </div>
          </div>

          {/* Operating Hours */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Operating Hours</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opening Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Closing Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Current Hours:</span> {openTime} - {closeTime}
              </p>
            </div>
          </div>

          {/* Cuisine Types */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Tag className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Cuisine Types</h2>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Select all cuisines that your restaurant serves ({selectedCuisines.length} selected)
              </p>
              {unresolvedCuisineValues.length > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-800 mb-2">
                    Existing cuisine values from API:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {unresolvedCuisineValues.map((value) => (
                      <span key={value} className="px-2 py-1 rounded bg-amber-100 text-amber-900 text-xs">
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {cuisineOptions.map((cuisine) => (
                  <button
                    key={cuisine.id}
                    onClick={() => toggleCuisine(cuisine)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isCuisineSelected(cuisine)
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cuisine.label}
                  </button>
                ))}
              </div>
              {cuisineOptions.length === 0 && (
                <p className="text-sm text-amber-700 mt-2">
                  No cuisine types found in master data.
                </p>
              )}
            </div>
          </div>

          {/* Delivery & Pricing */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Tag className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Delivery & Pricing</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Time</label>
                <input
                  type="text"
                  value={deliveryTime}
                  onChange={(e) => setDeliveryTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g. 30-45 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={selectedCurrencyId}
                  onChange={(e) => setSelectedCurrencyId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select Currency</option>
                  {currencyOptions.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} {currency.symbol ? `(${currency.symbol})` : ''} {currency.name ? `- ${currency.name}` : ''}
                    </option>
                  ))}
                </select>
                {currencyOptions.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">No currencies found in master data.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                <select
                  value={selectedPriceRangeId}
                  onChange={(e) => setSelectedPriceRangeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select Price Range</option>
                  {priceRangeOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.symbol ? `${item.symbol}${item.name}` : item.name}
                    </option>
                  ))}
                </select>
                {priceRangeOptions.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">No price ranges found in master data.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Order ({currencySymbol})</label>
                <input
                  type="number"
                  value={minimumOrder}
                  onChange={(e) => setMinimumOrder(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Fee ({currencySymbol})</label>
                <input
                  type="number"
                  value={deliveryFee}
                  onChange={(e) => setDeliveryFee(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Packaging Charges ({currencySymbol})</label>
                <input
                  type="number"
                  value={packagingCharges}
                  onChange={(e) => setPackagingCharges(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div ref={foodTypeDropdownRef} className="relative">
                <p className="text-sm font-medium text-gray-700 mb-2">Food Type</p>
                <button
                  type="button"
                  onClick={() => setFoodTypeDropdownOpen((prev) => !prev)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <span className={selectedFoodTypes.length ? 'text-gray-900' : 'text-gray-500'}>
                    {foodTypeDropdownLabel}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${foodTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {foodTypeDropdownOpen && (
                  <div className="absolute z-30 mt-2 w-full border border-gray-200 rounded-xl bg-white shadow-lg">
                    <div className="p-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={allVisibleFoodTypesSelected}
                          onChange={toggleAllVisibleFoodTypes}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={foodTypeSearch}
                            onChange={(e) => setFoodTypeSearch(e.target.value)}
                            placeholder="Search food type..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="max-h-44 overflow-y-auto py-1">
                      {filteredFoodTypeOptions.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-500">No food types found.</p>
                      ) : (
                        filteredFoodTypeOptions.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => toggleFoodType(item.id)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFoodTypes.includes(item.id)}
                              readOnly
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-800">{item.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {foodTypeOptions.length === 0 && (
                  <p className="text-sm text-amber-700 mt-2">No food types found in master data.</p>
                )}
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Building2 className="w-6 h-6 text-cyan-600" />
              </div>
              <h2 className="text-gray-900 font-semibold">Amenities & Facilities</h2>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Select all amenities ({selectedAmenities.length} selected)
              </p>
              <div className="flex flex-wrap gap-2">
                {amenityOptions.map((amenity) => (
                  <button
                    key={amenity.id}
                    onClick={() => toggleAmenity(amenity)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isAmenitySelected(amenity)
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {amenity.label}
                  </button>
                ))}
              </div>
            </div>
            {amenityOptions.length === 0 && (
              <p className="text-sm text-amber-700 mt-3">
                No amenities found in master data.
              </p>
            )}
          </div>
        </div>

        {/* Right Column - Media & Tags */}
        <div className="space-y-6">
          {/* Logo & Banner */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-gray-900 font-semibold mb-6">Restaurant Media</h2>
            {mediaError && (
              <p className="text-sm text-red-600 mb-3">{mediaError}</p>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleMediaUpload(file, 'logo');
                e.currentTarget.value = '';
              }}
            />
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleMediaUpload(file, 'banner');
                e.currentTarget.value = '';
              }}
            />

            {/* Logo */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Logo
              </label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer"
                onClick={() => logoInputRef.current?.click()}
              >
                {mediaLogo ? (
                  <div className="space-y-3">
                    <img
                      src={mediaLogo}
                      alt="Logo"
                      className="w-24 h-24 mx-auto rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        logoInputRef.current?.click();
                      }}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      {mediaUploading === 'logo' ? 'Uploading...' : 'Change Logo'}
                    </button>
                  </div>
                ) : (
                  <>
                    <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-2">
                      {mediaUploading === 'logo' ? 'Uploading...' : 'Click to upload logo'}
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  </>
                )}
              </div>
            </div>

            {/* Banner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Banner Image
              </label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer"
                onClick={() => bannerInputRef.current?.click()}
              >
                {mediaBanner ? (
                  <div className="space-y-3">
                    <img
                      src={mediaBanner}
                      alt="Banner"
                      className="w-full h-32 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        bannerInputRef.current?.click();
                      }}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      {mediaUploading === 'banner' ? 'Uploading...' : 'Change Banner'}
                    </button>
                  </div>
                ) : (
                  <>
                    <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-2">
                      {mediaUploading === 'banner' ? 'Uploading...' : 'Click to upload banner'}
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Photos & Videos */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-gray-900 font-semibold mb-4">Gallery</h2>
            <input
              ref={photosInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                void handleGalleryPhotosUpload(e.target.files);
                e.currentTarget.value = '';
              }}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleGalleryVideoSelect(file);
                e.currentTarget.value = '';
              }}
            />
            
            {/* Photos */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Photos</label>
                <button
                  type="button"
                  onClick={() => photosInputRef.current?.click()}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  {galleryUploading ? 'Uploading...' : 'Add Photos'}
                </button>
              </div>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer"
                onClick={() => photosInputRef.current?.click()}
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {galleryUploading ? 'Uploading photos...' : 'Upload restaurant photos'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Max 10 photos, 5MB each</p>
              </div>
              {galleryPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {galleryPhotos.map((photo, index) => (
                    <div key={photo.id || `${photo.mediaUrl}-${index}`} className="relative group">
                      <img
                        src={photo.mediaUrl}
                        alt={photo.fileName || `Gallery ${index + 1}`}
                        className="w-full h-20 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => void handleRemoveGalleryItem(photo, 'PHOTO')}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Videos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Videos</label>
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  {galleryVideoUploading ? 'Uploading...' : 'Add Video'}
                </button>
              </div>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer"
                onClick={() => videoInputRef.current?.click()}
              >
                <Video className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {galleryVideoUploading ? 'Uploading video...' : 'Upload promotional video'}
                </p>
                <p className="text-xs text-gray-500 mt-1">MP4, max 50MB</p>
              </div>
              {galleryVideos.length > 0 && (
                <div className="space-y-2 mt-3">
                  {galleryVideos.map((video, index) => (
                    <div key={video.id || `${video.mediaUrl}-${index}`} className="relative group">
                      <video controls className="w-full rounded-md border max-h-48 bg-black">
                        <source src={video.mediaUrl} />
                      </video>
                      <button
                        type="button"
                        onClick={() => void handleRemoveGalleryItem(video, 'VIDEO')}
                        className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Quick Info */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5" />
              <h3 className="font-semibold">Quick Tips</h3>
            </div>
            <ul className="space-y-2 text-sm text-green-50">
              <li>• Keep your information up to date</li>
              <li>• Add high-quality photos</li>
              <li>• Select accurate cuisines</li>
              <li>• List all available amenities</li>
              <li>• Update timings regularly</li>
            </ul>
          </div>
        </div>
      </div>

      <MapCoordinatePicker
        isOpen={showCoordinatePicker}
        onClose={() => setShowCoordinatePicker(false)}
        initialLocation={
          latitude.trim() !== '' &&
          longitude.trim() !== '' &&
          !Number.isNaN(Number(latitude)) &&
          !Number.isNaN(Number(longitude))
            ? { latitude: Number(latitude), longitude: Number(longitude) }
            : undefined
        }
        onConfirm={({ latitude: lat, longitude: lng }) => {
          setLatitude(String(lat));
          setLongitude(String(lng));
          setShowCoordinatePicker(false);
        }}
      />

      {/* Bottom Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-lg"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}

